// apps/web/src/store/app.ts
import type { DbUser, ApplicationProcess } from '@leangap/types'
import { migrateApplicationStatus } from '@/utils/backwardsCompatibility'
import type { ApplicationUpdate } from '@leangap/types'
import { useSyncExternalStore } from 'react'
import { authCache } from '@/services/auth-cache'
import { get as apiGet, patch as apiPatch, post as apiPost } from '@/services/api'
import { MeResponse as MeResponseSchema } from '@leangap/types'
import { serializeApplicationUpdate } from '@/store/serializer'
import { notifySaveFailure, type SaveSource } from '@/store/saveFeedback'

/**
 * Helper to check if the application is locked (any status beyond Draft).
 * Locked applications cannot be edited by the user.
 */
export function isApplicationLocked(process: ApplicationProcess | undefined | null): boolean {
	return process !== 'Draft' && process != null
}

type AppState = {
	user: DbUser | null
	dirty: boolean
	saving: boolean
	fileOperation?: boolean
	lastTimeSaved?: number
}

let state: AppState = {
	user: null,
	dirty: false,
	saving: false,
	fileOperation: false,
	lastTimeSaved: undefined,
}
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((fn) => fn())
const set = (next: Partial<AppState>) => {
	state = { ...state, ...next }
	emit()
}

export const appStore = {
	// init(user: DbUser) {
	//   // Normalize application_status to object format if needed
	//   const normalizedUser = { ...user }
	//   if (Array.isArray(user.application_status) && user.application_status.length === 4) {
	//     normalizedUser.application_status = {
	//       general: !!user.application_status[0],
	//       background: !!user.application_status[1],
	//       program: !!user.application_status[2],
	//       aid: !!user.application_status[3],
	//     }
	//   }
	//   state = { user: normalizedUser, dirty: false, saving: false }
	//   emit()
	// },
	init(user: DbUser) {
		// --- BACKWARDS COMPATIBILITY ---
		// This call migrates legacy array application_status to object format for old users.
		// Once all users are migrated, REMOVE the call to migrateApplicationStatus and this comment.
		const normalizedUser = migrateApplicationStatus(user) as DbUser
		// initialization snapshot (kept for debugging in dev builds if needed)
		// If the server provides a lastTimeSaved value, use it so the UI shows persisted timestamp.
		state = {
			user: normalizedUser,
			dirty: false,
			saving: false,
			lastTimeSaved: normalizedUser.lastTimeSaved ?? undefined,
		}
		emit()
	},
	snapshot(): AppState {
		return state
	},
	subscribe(fn: () => void) {
		listeners.add(fn)
		return () => listeners.delete(fn)
	},
	// patch(p: Partial<DbUser>) {
	//   if (!state.user) return
	//   const nextUser = { ...state.user, ...p }
	//   if (Array.isArray(nextUser.application_status) && nextUser.application_status.length === 4) {
	//     nextUser.application_status = {
	//       general: !!nextUser.application_status[0],
	//       background: !!nextUser.application_status[1],
	//       program: !!nextUser.application_status[2],
	//       aid: !!nextUser.application_status[3],
	//     }
	//   }
	//   state = { ...state, user: nextUser, dirty: true }
	//   emit()
	// },
	patch(p: Partial<DbUser>) {
		if (!state.user) return
		// Do not allow patches when application is locked (Submitted, Admitted, Not Accepted)
		if (isApplicationLocked(state.user.application_process)) {
			// no-op: prevent local mutations after submission
			return
		}
		// --- BACKWARDS COMPATIBILITY ---
		// This call migrates legacy array application_status to object format for old users.
		// Once all users are migrated, REMOVE the call to migrateApplicationStatus and this comment.
		const prev = state.user
		const nextUser = migrateApplicationStatus({ ...state.user, ...p })
		// patch snapshot: intentionally not logged in test runs

		// Only mark dirty and update state if something actually changed.
		let changed = false
		// Check keys provided in the patch for changes
		for (const key of Object.keys(p)) {
			if (key === 'application_status') {
				const a = JSON.stringify(prev.application_status ?? {})
				const b = JSON.stringify(
					(p as unknown as Record<string, unknown>).application_status ??
						prev.application_status ??
						{}
				)
				if (a !== b) changed = true
			} else {
				const k = key as keyof DbUser
				const prevRec = prev as unknown as Record<string, unknown>
				const nextRec = nextUser as unknown as Record<string, unknown>
				if (prevRec[k as string] !== nextRec[k as string]) changed = true
			}
			if (changed) break
		}

		if (changed) {
			state = { ...state, user: nextUser, dirty: true }
			emit()
		} else {
			// No-op patch (no actual changes) — do nothing to avoid autosave
		}
	},
	reset() {
		state = { user: null, dirty: false, saving: false }
		emit()
	},
	sync(user: DbUser) {
		// Prefer server-provided lastTimeSaved (ms) when available, otherwise fall back to now.
		state = { user, dirty: false, saving: false, lastTimeSaved: user.lastTimeSaved ?? Date.now() }
		authCache.set(user)
		emit()
	},
	// Allow external callers to mark the saving flag when performing related operations
	setSaving(val: boolean) {
		state = { ...state, saving: val }
		emit()
	},
	// Mark that a file upload/delete operation is in progress (UI can block on this)
	setFileOperation(val: boolean) {
		state = { ...state, fileOperation: val }
		emit()
	},
	async save(opts?: {
		submit?: boolean
		/** Who triggered the save; drives how failures are reported to the user. */
		source?: SaveSource
		onLogoutNavigate?: (path: string, opts?: { replace?: boolean }) => void
	}) {
		if (!state.user || state.saving) return
		// Prevent save if application is locked (unless submitting for first time)
		if (isApplicationLocked(state.user.application_process)) return
		set({ saving: true })
		try {
			const payload: ApplicationUpdate & { submit?: boolean } = {
				...serializeApplicationUpdate(state.user),
				...(opts?.submit ? { submit: true } : {}),
			}
			const res = await apiPatch('/app/save', payload, MeResponseSchema)
			const typed = res as { user: DbUser }

			state = {
				user: typed.user,
				dirty: false,
				saving: false,
				lastTimeSaved: typed.user.lastTimeSaved ?? Date.now(),
			}
			authCache.set(typed.user)
			// Return server-side user
			emit()
			return typed.user
		} catch (e) {
			set({ saving: false })
			// If the backend indicates the user can't be found, clear local
			// auth state and optionally navigate to login so the UI returns to
			// the public entrypoint. The api layer throws HttpError instances
			// with a body we can inspect.
			try {
				const err = e as { status?: number; body?: unknown }
				const errorCode =
					err?.body && typeof err.body === 'object'
						? (err.body as Record<string, unknown>).error
						: undefined

				// The server already holds a submitted application. This happens
				// when a submit reached the server but its response never made it
				// back (API restarted mid-request, network drop) and the user
				// retried: Mongo (and HubSpot) were updated exactly once, only the
				// client is behind. Re-sync from /me so the UI converges on the
				// server state instead of surfacing an error for a completed submit.
				if (err?.status === 409 && errorCode === 'already_submitted') {
					const resp = (await apiGet('/me', MeResponseSchema)) as { user: DbUser }
					appStore.sync(resp.user)
					return resp.user
				}

				if (err?.status === 404 && err?.body && typeof err.body === 'object') {
					const b = err.body as Record<string, unknown>
					if (b.error === 'user_not_found') {
						try {
							void apiPost('/auth/logout', undefined).catch(() => {})
						} catch {
							// ignore synchronous errors (shouldn't happen)
						}

						// Clear local session/cache
						try {
							authCache.clear()
						} catch {
							// ignore
						}
						// Reset in-memory app store using the public reset helper
						try {
							appStore.reset()
						} catch {
							// fallback: directly set state if reset isn't available for some reason
							state = { user: null, dirty: false, saving: false }
							emit()
						}

						// Navigate to login if provided, otherwise fallback to a hard redirect
						const nav = opts?.onLogoutNavigate
						if (nav) {
							nav('/login', { replace: true })
						} else {
							try {
								// Use replace to avoid leaving an unusable history entry
								window.location.replace('/login')
							} catch {
								// ignore if running in a non-browser environment (tests)
							}
						}

						// swallow original error since we've handled logout behavior
						return
					}
				}
			} catch {
				// ignore errors during error handling
			}

			// Tell the user what happened and what to do next. Anything that
			// reaches this point was not recovered above; local edits stay dirty
			// so autosave / Save / Submit can retry.
			try {
				notifySaveFailure(e, { submit: !!opts?.submit, source: opts?.source ?? 'manual' })
			} catch {
				// never let feedback break the save path
			}
			throw e
		}
	},
}

export function useAppStore(): AppState {
	return useSyncExternalStore(appStore.subscribe, appStore.snapshot, appStore.snapshot)
}
